// tutorSubjects — curated "things past learners commonly get stuck on" that
// surface as one-tap chips inside the Tutor sheet. The first version is
// AI-seeded (we asked an LLM to summarize the most frequent beginner
// confusions per surface) so users get value before we have enough real
// chat history to mine. As we collect anonymized tutor question logs we'll
// replace these with mined-from-history clusters — keeping the same shape
// keeps the UI stable when we swap the source.
//
// Matching: the TutorSheet picks the first bucket whose `match` predicate
// returns true given the current location pathname. `default` always wins
// last so there's always something to show.

export type TutorSubject = {
  /** Short label shown on the chip. Keep <= 28 chars so chips wrap nicely. */
  label: string;
  /** Full prompt sent to the tutor when tapped. */
  prompt: string;
};

export type TutorSubjectBucket = {
  id: string;
  /** Human label shown above the chips ("Common questions about Playbooks"). */
  heading: string;
  /** Pathname predicate. Return true to use this bucket. */
  match: (pathname: string) => boolean;
  subjects: TutorSubject[];
};

const PLAYBOOK_RUNNER_SUBJECTS: TutorSubject[] = [
  {
    label: "What does each step actually do?",
    prompt:
      "Walk me through what each step in this playbook is for, in plain language. Why are these specific models picked?",
  },
  {
    label: "Why did my output look generic?",
    prompt:
      "My last AI response felt generic or surface-level. What's the most common reason that happens, and how do I fix it for the step I'm on?",
  },
  {
    label: "When should I switch models mid-playbook?",
    prompt:
      "When does it actually pay to switch to a different model partway through a playbook? Give me 2-3 concrete signals.",
  },
  {
    label: "How do I reuse this output later?",
    prompt:
      "Once I finish this playbook, what's the smartest way to save or reuse the result so I don't lose the work?",
  },
];

const HUB_SUBJECTS: TutorSubject[] = [
  {
    label: "Chat vs Research vs Analyze — when?",
    prompt:
      "Quick rule of thumb: when should I use Chat vs Research vs Analyze in the Hub? Give me one trigger sentence per mode.",
  },
  {
    label: "Why are my credits burning fast?",
    prompt:
      "What are the top 3 reasons people burn through credits faster than expected, and what's the easiest fix for each?",
  },
  {
    label: "How do I write a better prompt?",
    prompt:
      "Give me a 4-bullet checklist for upgrading any prompt from 'meh' to 'great' before I hit send.",
  },
];

const MASTERY_SUBJECTS: TutorSubject[] = [
  {
    label: "Where should I start on the roadmap?",
    prompt:
      "Given I'm new to the AI Mastery Roadmap, what's the single best skill to launch first and why?",
  },
  {
    label: "How long does each skill take?",
    prompt:
      "Roughly how much real time do most learners spend on a single mastery skill, and what makes some take longer?",
  },
  {
    label: "What unlocks the certificate?",
    prompt: "Plain-language: what do I have to do to unlock my Mastery certificate?",
  },
];

const SQUAD_SUBJECTS: TutorSubject[] = [
  {
    label: "How do I assign a step to a teammate?",
    prompt:
      "Walk me through assigning or reassigning a protocol step to a squad member, and what they'll see when I do.",
  },
  {
    label: "What's the point of a squad protocol?",
    prompt:
      "What does running a 'squad protocol' get me that just chatting individually wouldn't? Keep it concrete.",
  },
];

const DEFAULT_SUBJECTS: TutorSubject[] = [
  {
    label: "What's the fastest first win?",
    prompt:
      "I want one quick win in the next 5 minutes that proves this app is useful. What should I do RIGHT NOW from this page?",
  },
  {
    label: "Which model should I default to?",
    prompt:
      "If I'm new and I don't know which AI model to pick, what's the safest default and when should I deviate?",
  },
  {
    label: "How do I save what I make?",
    prompt:
      "Where does my work get saved across the app — chats, playbooks, projects — and how do I find it later?",
  },
  {
    label: "Free vs paid — what changes?",
    prompt:
      "What changes between the free plan and paid plans here? Just give me the 3 biggest practical differences.",
  },
];

const BUCKETS: TutorSubjectBucket[] = [
  {
    id: "home-runner",
    heading: "Common playbook questions",
    match: (p) => p === "/" || p.startsWith("/quests") || p.startsWith("/learn"),
    subjects: PLAYBOOK_RUNNER_SUBJECTS,
  },
  {
    id: "hub",
    heading: "Hub questions other learners ask",
    match: (p) => p.startsWith("/workspace"),
    subjects: HUB_SUBJECTS,
  },
  {
    id: "mastery",
    heading: "Mastery roadmap FAQs",
    match: (p) => p.startsWith("/roadmap") || p.startsWith("/cert"),
    subjects: MASTERY_SUBJECTS,
  },
  {
    id: "squads",
    heading: "Squad collaboration FAQs",
    match: (p) => p.startsWith("/squads") || p.startsWith("/jam"),
    subjects: SQUAD_SUBJECTS,
  },
  {
    id: "default",
    heading: "Where most people start",
    match: () => true,
    subjects: DEFAULT_SUBJECTS,
  },
];

/** Pick the first matching bucket for a route. Always returns something. */
export function getTutorSubjectsForPath(pathname: string): TutorSubjectBucket {
  return BUCKETS.find((b) => b.match(pathname)) ?? BUCKETS[BUCKETS.length - 1];
}